import React, { useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    AppBar,
    Box,
    Drawer,
    IconButton,
    Toolbar,
    Typography,
    Divider,
    Avatar,
    Menu,
    MenuItem,
    Theme,
    useMediaQuery,
    useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Sidebar from './Sidebar';

// ============================================================================
// Constants
// ============================================================================
const drawerWidth = 240;

// ============================================================================
// Interfaces
// ============================================================================
interface LayoutProps {
    children: ReactNode;
}

// ============================================================================
// Component
// ============================================================================
export default function Layout({ children }: LayoutProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [desktopDrawerOpen, setDesktopDrawerOpen] = useState(true);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleDrawerToggle = () => {
        if (isMobile) {
            setMobileDrawerOpen(!mobileDrawerOpen);
        } else {
            setDesktopDrawerOpen(!desktopDrawerOpen);
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleMenuClose();
        logout();
        navigate('/login');
    };

    const drawerContent = <Sidebar drawerOpen={isMobile ? true : desktopDrawerOpen} toggleDrawer={handleDrawerToggle} />;

    return (
        <Box sx={{ display: 'flex' }}>
            <AppBar
                position="fixed"
                sx={{
                    zIndex: (theme: Theme) => theme.zIndex.drawer + 1,
                    backgroundColor: 'white',
                    color: 'text.primary',
                    boxShadow: 1
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ 
                            mr: 2,
                            display: { md: isMobile ? 'block' : 'none' }
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        WrkFlowPro
                    </Typography>
                    <IconButton
                        onClick={handleMenuOpen}
                        size="large"
                        edge="end"
                        aria-label="account menu"
                        aria-haspopup="true"
                    >
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                            {user?.username?.charAt(0) || <AccountCircle />}
                        </Avatar>
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        onClick={handleMenuClose}
                    >
                        <MenuItem disabled>
                            <Typography variant="body2">{user?.email}</Typography>
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={handleLogout}>Logout</MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            {/* Mobile drawer */}
            <Drawer
                variant="temporary"
                open={mobileDrawerOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile
                }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: drawerWidth,
                    },
                }}
            >
                {drawerContent}
            </Drawer>

            {/* Desktop drawer */}
            <Drawer
                variant="permanent"
                open={desktopDrawerOpen}
                sx={{
                    display: { xs: 'none', md: 'block' },
                    width: desktopDrawerOpen ? drawerWidth : 72,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: desktopDrawerOpen ? drawerWidth : 72,
                        boxSizing: 'border-box',
                        transition: (theme: Theme) =>
                            theme.transitions.create(['width'], {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                        overflowX: 'hidden',
                    },
                }}
            >
                {drawerContent}
            </Drawer>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 1, sm: 2, md: 3 },
                    width: { 
                        xs: '100%',
                        md: desktopDrawerOpen ? `calc(100% - ${drawerWidth}px)` : `calc(100% - 72px)`
                    },
                    minHeight: '100vh',
                    mt: { xs: 7, sm: 8 } // Account for app bar height
                }}
            >
                {children}
            </Box>
        </Box>
    );
}
